import React, {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Close from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import {
  BasicsEditor,
  BooleanMapper,
  DisplayOrderEditor,
  GroupEditor,
  TypeIcon,
} from 'features/facets/components/FacetEditorUi';
import facetPolicyDraftReducer, {
  assignmentChanged,
  assignmentCleared,
  assignmentsClearedAll,
  conflictRebased,
  discarded,
  draftPatched,
  facetTypeChanged,
  groupDeleted,
  parseFacetPolicyDraft,
  policyFailed,
  policyRequested,
  policySucceeded,
  recoveryRestored,
  saveFailed,
  saveErrorDismissed,
  saveRequested,
  saveSucceeded,
  scopeOpened,
  selectIsDirty,
  selectSavePayload,
  serializeFacetPolicyDraft,
  valuePolicyPatched,
} from './facetPolicyDraftSlice';
import NumberFacetPolicyEditor from './NumberFacetPolicyEditor';
import { useFacetPolicyTranslation } from './facetPolicyI18n';
import styles from './CanonicalFacetPolicyPage.module.scss';

const DEFAULT_CAPABILITIES = {
  facet_policy_v1: true,
  facet_numeric_v1: true,
  facet_string_presentation_v1: false,
};

function effectiveCanonicalValue(item, delta) {
  const id = String(item.source_value_id);
  if (Object.prototype.hasOwnProperty.call(delta.assignmentChanges, id)) {
    return delta.assignmentChanges[id];
  }
  if (delta.clearAllAssignments || delta.clearAssignmentIds[id]) return null;
  return item.canonical_value == null ? null : String(item.canonical_value);
}

function issueSourceId(issue, delta) {
  if (issue && issue.source_value_id != null) return String(issue.source_value_id);
  const path = issue && typeof issue.path === 'string' ? issue.path : '';
  const direct = path.match(/source(?:_value)?(?:_id)?(?:[.:]|\[)([A-Za-z0-9._:-]+)/);
  if (direct) return direct[1];
  const indexed = path.match(/assignment_changes\[(\d+)\]/);
  if (!indexed) return null;
  return Object.keys(delta.assignmentChanges).sort()[Number(indexed[1])] || null;
}

export function BooleanBulkAssignmentEditor({
  api,
  facetKey,
  delta,
  dispatch,
  validationIssues,
}) {
  const t = useFacetPolicyTranslation();
  const [query, setQuery] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const requestSequence = useRef(0);

  const load = useCallback(async (cursor = null, append = false) => {
    if (!api || !facetKey) return;
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setStatus('loading');
    setError(null);
    try {
      const response = await api.searchValues({
        facet_key: String(facetKey),
        query,
        assignment: assignmentFilter,
        cursor,
        limit: 100,
      });
      if (requestSequence.current !== sequence) return;
      const data = response && response.data ? response.data : {};
      const incoming = Array.isArray(data.items) ? data.items : [];
      setRows((current) => {
        const merged = append ? current.concat(incoming) : incoming;
        const seen = {};
        return merged.filter((item) => {
          const id = String(item.source_value_id);
          if (seen[id]) return false;
          seen[id] = true;
          return true;
        });
      });
      if (!append) setSelected({});
      setNextCursor(data.next_cursor || null);
      setStatus('succeeded');
    } catch (loadError) {
      if (requestSequence.current !== sequence) return;
      setError(loadError && loadError.message ? loadError.message : t('facets.policy.error.valuesLoad', 'Facet values failed to load.'));
      setStatus('failed');
    }
  }, [api, assignmentFilter, facetKey, query, t]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleIds = rows.map(item => String(item.source_value_id));
  const selectedIds = visibleIds.filter(id => selected[id]);
  const allVisibleSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length;
  const unresolved = rows.filter(item => effectiveCanonicalValue(item, delta) == null).length;
  const issuesBySource = useMemo(() => (validationIssues || []).reduce((result, issue) => {
    const id = issueSourceId(issue, delta);
    if (id) result[id] = issue.message || issue.reason || t('facets.policy.boolean.invalid', 'Assignment is invalid.');
    return result;
  }, {}), [delta, t, validationIssues]);

  const assignSelected = useCallback((canonicalValue) => {
    selectedIds.forEach(sourceValueId => dispatch(assignmentChanged({ sourceValueId, canonicalValue })));
    setSelected({});
  }, [dispatch, selectedIds]);

  const clearSelected = useCallback(() => {
    selectedIds.forEach(sourceValueId => dispatch(assignmentCleared({ sourceValueId })));
    setSelected({});
  }, [dispatch, selectedIds]);

  return (
    <section className={styles.booleanEditor} aria-label={t('facets.policy.boolean.title', 'Boolean bulk assignment editor')}>
      <div className={styles.booleanFilters} role="search" aria-label={t('facets.policy.boolean.filters', 'Source value filters')}>
        <TextField
          id="boolean-source-search"
          label={t('facets.policy.boolean.search', 'Search source values')}
          value={query}
          variant="outlined"
          size="small"
          fullWidth
          onChange={event => setQuery(event.target.value)}
        />
        <TextField
          id="boolean-assignment-filter"
          label={t('facets.policy.boolean.mappedState', 'Mapped state')}
          value={assignmentFilter}
          variant="outlined"
          size="small"
          select
          SelectProps={{ native: true }}
          onChange={event => setAssignmentFilter(event.target.value)}
        >
          <option value="all">{t('facets.policy.boolean.all', 'All')}</option>
          <option value="assigned">{t('facets.policy.boolean.assigned', 'Assigned')}</option>
          <option value="unassigned">{t('facets.policy.boolean.unassigned', 'Unassigned')}</option>
        </TextField>
        <Button variant="outlined" color="primary" onClick={() => load()}>{t('facets.policy.boolean.applyFilters', 'Apply filters')}</Button>
      </div>

      <div className={styles.booleanSummary} role="status" aria-live="polite">
        {status === 'loading' && <CircularProgress className={styles.inlineProgress} size={16} />}
        {t(unresolved === 1 ? 'facets.policy.boolean.oneUnassigned' : 'facets.policy.boolean.manyUnassigned', unresolved === 1 ? '{count} loaded value unassigned.{more}' : '{count} loaded values unassigned.{more}', {
          count: unresolved,
          more: nextCursor ? ` ${t('facets.policy.boolean.moreAvailable', 'More values are available.')}` : '',
        })}
      </div>
      {error && <div role="alert">{error}</div>}

      <div className={styles.booleanToolbar} role="toolbar" aria-label={t('facets.policy.boolean.bulk', 'Bulk boolean assignments')}>
        <ButtonGroup size="small" variant="outlined" color="primary">
          <Button disabled={selectedIds.length === 0} onClick={() => assignSelected('true')}>{t('facets.policy.boolean.assignTrue', 'Assign selected True')}</Button>
          <Button disabled={selectedIds.length === 0} onClick={() => assignSelected('false')}>{t('facets.policy.boolean.assignFalse', 'Assign selected False')}</Button>
          <Button disabled={selectedIds.length === 0} onClick={clearSelected}>{t('facets.policy.boolean.clearSelected', 'Clear selected')}</Button>
        </ButtonGroup>
        <Button size="small" variant="outlined" color="primary" onClick={() => dispatch(assignmentsClearedAll())}>{t('facets.policy.boolean.clearAll', 'Clear all assignments')}</Button>
      </div>

      <div className={styles.booleanTableWrap}>
      <table className={styles.booleanTable}>
        <caption>{t('facets.policy.boolean.caption', 'Source values grouped as Unassigned, True, or False')}</caption>
        <thead>
          <tr>
            <th scope="col">
              <Checkbox
                color="primary"
                inputProps={{ 'aria-label': t('facets.policy.boolean.selectVisible', 'Select visible values') }}
                checked={allVisibleSelected}
                onChange={event => setSelected(event.target.checked
                  ? visibleIds.reduce((result, id) => ({ ...result, [id]: true }), {}) : {})}
              />
            </th>
            <th scope="col">{t('facets.policy.boolean.sourceValue', 'Source value')}</th>
            <th scope="col">{t('facets.policy.boolean.products', 'Products')}</th>
            <th scope="col">{t('facets.policy.boolean.assignment', 'Assignment')}</th>
            <th scope="col">{t('facets.policy.boolean.actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item) => {
            const id = String(item.source_value_id);
            const sourceLabel = item.source_label || item.label || id;
            const canonicalValue = effectiveCanonicalValue(item, delta);
            const state = canonicalValue === 'true' ? t('facets.policy.boolean.true', 'True')
              : canonicalValue === 'false' ? t('facets.policy.boolean.false', 'False') : t('facets.policy.boolean.unassigned', 'Unassigned');
            const issueId = issuesBySource[id] ? `boolean-assignment-issue-${id.replace(/[^A-Za-z0-9_-]/g, '-')}` : undefined;
            return (
              <tr key={id} aria-describedby={issueId}>
                <td>
                  <Checkbox
                    color="primary"
                    inputProps={{ 'aria-label': t('facets.policy.boolean.selectValue', 'Select {label} ({id})', { label: sourceLabel, id }) }}
                    checked={selected[id] === true}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setSelected(current => ({ ...current, [id]: checked }));
                    }}
                  />
                </td>
                <th scope="row">{sourceLabel}<small>{` (${id})`}</small></th>
                <td>{Number(item.count || 0)}</td>
                <td><Chip className={styles.assignmentChip} size="small" label={state} /></td>
                <td className={styles.booleanActions}>
                  <ButtonGroup size="small" variant="outlined" color="primary">
                    <Button aria-label={t('facets.policy.boolean.assignValueTrue', 'Assign {label} ({id}) True', { label: sourceLabel, id })} onClick={() => dispatch(assignmentChanged({ sourceValueId: id, canonicalValue: 'true' }))}>{t('facets.policy.boolean.true', 'True')}</Button>
                    <Button aria-label={t('facets.policy.boolean.assignValueFalse', 'Assign {label} ({id}) False', { label: sourceLabel, id })} onClick={() => dispatch(assignmentChanged({ sourceValueId: id, canonicalValue: 'false' }))}>{t('facets.policy.boolean.false', 'False')}</Button>
                    <Button aria-label={t('facets.policy.boolean.clearValue', 'Clear {label} ({id}) assignment', { label: sourceLabel, id })} onClick={() => dispatch(assignmentCleared({ sourceValueId: id }))}>{t('facets.policy.boolean.unassigned', 'Unassigned')}</Button>
                  </ButtonGroup>
                  {issuesBySource[id] && <div id={issueId} role="alert">{issuesBySource[id]}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {nextCursor && (
        <Button size="small" variant="outlined" color="primary" disabled={status === 'loading'} onClick={() => load(nextCursor, true)}>
          {status === 'loading' ? t('common.loading', 'Loading…') : t('facets.policy.boolean.loadMore', 'Load more values')}
        </Button>
      )}
    </section>
  );
}

function initialDraftState() {
  return facetPolicyDraftReducer(undefined, { type: '@@facet-policy/init' });
}

function errorMessage(error, fallback) {
  return error && error.message ? error.message : fallback;
}

function requestId(prefix, sequence) {
  return `${prefix}-${sequence}`;
}

function generatedMutationId() {
  return `facet-policy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function trapTabInside(event) {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(event.currentTarget.querySelectorAll(
    'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
  )).filter(element => element.getClientRects().length > 0);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function StringPresentationPicker({ value, onChange, enabled }) {
  const t = useFacetPolicyTranslation();
  const options = [
    {
      id: 'list',
      title: t('facets.policy.presentation.list', 'Standard'),
      description: t('facets.policy.presentation.listDescription', 'Checkbox list for brands, categories, and other values.'),
    },
    {
      id: 'size',
      title: t('facets.policy.presentation.size', 'Compact'),
      description: t('facets.policy.presentation.sizeDescription', 'Space-saving buttons for sizes and other short values.'),
    },
  ];

  return (
    <section className="fp-form-card fp-presentation-card" aria-labelledby="facet-presentation-heading">
      <header>
        <span className="fp-eyebrow">{t('facets.policy.presentation.eyebrow', 'Display')}</span>
        <h3 id="facet-presentation-heading">{t('facets.policy.presentation.heading', 'Facet style')}</h3>
        <p>{enabled
          ? t('facets.policy.presentation.description', 'Choose how filter values are displayed. This does not change the indexed data.')
          : t('facets.policy.presentation.unavailable', 'Update the store plugin to choose a facet style.')}
        </p>
      </header>
      <div
        className="fp-presentation-options"
        role="radiogroup"
        aria-labelledby="facet-presentation-heading"
      >
        {options.map(option => (
          <button
            type="button"
            role="radio"
            key={option.id}
            className={value === option.id ? 'is-active' : ''}
            aria-checked={value === option.id}
            disabled={!enabled}
            onClick={() => onChange(option.id)}
          >
            <span className={`fp-presentation-preview is-${option.id}`} aria-hidden="true">
              <i /><i /><i />
            </span>
            <span><strong>{option.title}</strong><small>{option.description}</small></span>
            <span className="fp-choice-mark"><CheckIcon /></span>
          </button>
        ))}
      </div>
    </section>
  );
}

StringPresentationPicker.propTypes = {
  value: PropTypes.oneOf(['list', 'size']).isRequired,
  onChange: PropTypes.func.isRequired,
  enabled: PropTypes.bool.isRequired,
};

export default function FacetPolicyEditorShell({
  api,
  facetKey,
  shopId,
  languageId,
  capabilities,
  recovery,
  onRecoveryChange,
  onDirtyChange,
  onClose,
  onSaved,
  onRequestReindex,
  mutationIdFactory,
}) {
  const t = useFacetPolicyTranslation();
  const [state, dispatch] = useReducer(facetPolicyDraftReducer, undefined, initialDraftState);
  const [section, setSection] = useState('basics');
  const [closeGuard, setCloseGuard] = useState(false);
  const [effect, setEffect] = useState(null);
  const [validationIssues, setValidationIssues] = useState([]);
  const [numericValidation, setNumericValidation] = useState(null);
  const [numericValidationEpoch, setNumericValidationEpoch] = useState(0);
  const [groupValues, setGroupValues] = useState({ items: [], nextCursor: null, status: 'idle', query: '' });
  const loadSequence = useRef(0);
  const groupValueSequence = useRef(0);
  const saveSequence = useRef(0);
  const recoveryRef = useRef(recovery);
  const editorRef = useRef(null);
  const closeRef = useRef(null);
  const keepEditingRef = useRef(null);
  const scope = useMemo(() => ({
    shop: String(shopId),
    language: String(languageId),
    document_type: 'product',
  }), [languageId, shopId]);
  const policyEnabled = capabilities.facet_policy_v1 === true;
  const numericEnabled = capabilities.facet_numeric_v1 === true;
  const stringPresentationEnabled = capabilities.facet_string_presentation_v1 === true;
  const dirty = selectIsDirty(state);
  const openDraftType = state.openPolicy.draft ? state.openPolicy.draft.type : null;
  const baselinePolicy = state.openPolicy.baseline && state.openPolicy.baseline.policy;
  const baselineNumeric = baselinePolicy
    && baselinePolicy.value_policy
    && state.openPolicy.baseline.policy.value_policy.numeric;
  const requiresNumericValidation = openDraftType === 'number'
    && baselinePolicy
    && (baselinePolicy.type !== 'number'
      || !baselineNumeric || baselineNumeric.state !== 'active');

  const loadGroupValues = useCallback(async (query = '', cursor = null, append = false) => {
    if (!api || typeof api.searchValues !== 'function' || !facetKey) return;
    groupValueSequence.current += 1;
    const sequence = groupValueSequence.current;
    setGroupValues(current => ({ ...current, status: 'loading', query }));
    try {
      let nextCursor = cursor;
      let items = [];
      const cursors = new Set();
      do {
        const response = await api.searchValues({
          facet_key: String(facetKey), query, assignment: 'all', cursor: nextCursor, limit: 100,
        });
        if (groupValueSequence.current !== sequence) return;
        const data = response && response.data ? response.data : {};
        items = items.concat(data.items || []);
        const next = data.next_cursor == null ? null : String(data.next_cursor);
        if (!next || cursors.has(next)) nextCursor = null;
        else {
          cursors.add(next);
          nextCursor = next;
        }
      } while (nextCursor);
      setGroupValues(current => ({
        items: (append ? current.items.concat(items) : items).filter((item, index, values) => (
          values.findIndex(candidate => String(candidate.source_value_id) === String(item.source_value_id)) === index
        )),
        nextCursor: null, status: 'succeeded', query,
      }));
    } catch (error) {
      if (groupValueSequence.current !== sequence) return;
      setGroupValues(current => ({ ...current, status: 'failed' }));
    }
  }, [api, facetKey]);

  useEffect(() => {
    if ((section === 'values' || section === 'display')
      && (openDraftType === 'string' || openDraftType === 'color' || openDraftType === 'boolean')) {
      loadGroupValues('', null, false);
    }
  }, [loadGroupValues, openDraftType, section]);

  useEffect(() => {
    dispatch(scopeOpened(scope));
    setNumericValidation(null);
  }, [scope]);

  useEffect(() => { setNumericValidation(null); }, [facetKey]);

  useEffect(() => {
    recoveryRef.current = recovery;
  }, [recovery]);

  useEffect(() => {
    let current = true;
    if (!facetKey || !api || !policyEnabled) return undefined;
    loadSequence.current += 1;
    const id = requestId('policy', loadSequence.current);
    dispatch(policyRequested({ requestId: id }));
    api.getPolicy({ facet_key: String(facetKey) }).then((response) => {
      if (!current) return;
      const data = response && response.data ? response.data : {};
      const policy = data.policy || null;
      if (!policy) {
        dispatch(policySucceeded({
          requestId: id,
          facetKey: String(facetKey),
          policy: null,
          revision: data.policy_revision,
        }));
        return;
      }
      dispatch(policySucceeded({
        requestId: id,
        facetKey: String(facetKey),
        policy,
        revision: data.policy_revision,
      }));
      setEffect(data.effect || null);
      const recovered = recoveryRef.current
        ? parseFacetPolicyDraft(recoveryRef.current, scope) : null;
      if (recovered && recovered.facetKey === String(facetKey)) {
        dispatch(recoveryRestored(recovered));
      }
    }).catch((error) => {
      if (!current) return;
      dispatch(policyFailed({
        requestId: id,
        error: errorMessage(error, t('facets.policy.error.policyLoad', 'Facet policy failed to load.')),
      }));
    });
    return () => { current = false; };
  }, [api, facetKey, policyEnabled, scope, t]);

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
    if (onRecoveryChange && state.openPolicy.baseline) {
      onRecoveryChange(dirty ? serializeFacetPolicyDraft(state) : null);
    }
  }, [dirty, onDirtyChange, onRecoveryChange, state]);

  useEffect(() => {
    if (!dirty || typeof window === 'undefined') return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const patchMetadata = useCallback((patch) => {
    dispatch(draftPatched(patch));
  }, []);

  const patchValuePolicy = useCallback((patch) => {
    dispatch(valuePolicyPatched(patch));
  }, []);

  const save = useCallback(async () => {
    if (!api || !dirty || state.save.status === 'saving') return;
    saveSequence.current += 1;
    const id = requestId('save', saveSequence.current);
    const action = saveRequested({ requestId: id, mutationId: mutationIdFactory() });
    const pendingState = facetPolicyDraftReducer(state, action);
    const selectedPayload = selectSavePayload(pendingState);
    const baselineNumericPolicy = state.openPolicy.baseline.policy.value_policy
      && state.openPolicy.baseline.policy.value_policy.numeric;
    const needsNumericValidation = state.openPolicy.draft.type === 'number'
      && (state.openPolicy.baseline.policy.type !== 'number'
        || !baselineNumericPolicy || baselineNumericPolicy.state !== 'active');
    const payload = needsNumericValidation
      ? { ...selectedPayload, validation_token: numericValidation && numericValidation.token }
      : selectedPayload;
    if (needsNumericValidation && !payload.validation_token) return;
    dispatch(action);
    let data;
    try {
      const response = await api.updatePolicy(payload);
      data = response && response.data ? response.data : {};
    } catch (error) {
      const details = error && error.details && typeof error.details === 'object' ? error.details : {};
      const code = error && error.code ? error.code : 'INTERNAL_ERROR';
      if (code === 'VALIDATION_STALE') {
        setNumericValidation(null);
        setNumericValidationEpoch(epoch => epoch + 1);
      }
      setValidationIssues(Array.isArray(details.issues) ? details.issues : []);
      if (code === 'REVISION_CONFLICT') {
        try {
          const latestResponse = await api.getPolicy({ facet_key: String(facetKey) });
          const latest = latestResponse && latestResponse.data ? latestResponse.data : {};
          if (latest.policy) {
            dispatch(conflictRebased({
              requestId: id,
              policy: latest.policy,
              revision: latest.policy_revision,
            }));
            return;
          }
        } catch (_refreshError) {
          // The local draft remains recoverable even when the refresh fails.
        }
      }
      dispatch(saveFailed({
        requestId: id,
        code,
        message: errorMessage(error, t('facets.policy.error.policySave', 'Facet policy failed to save.')),
      }));
      return;
    }

    dispatch(saveSucceeded({
      requestId: id,
      policy: data.policy,
      revision: data.policy_revision,
    }));
    setValidationIssues([]);
    setNumericValidation(null);
    setEffect(data.effect || null);
    try {
      if (onRecoveryChange) onRecoveryChange(null);
      onSaved({
        facetKey: String(facetKey),
        policy: data.policy,
        policyRevision: data.policy_revision,
        effect: data.effect || null,
      });
    } finally {
      onClose();
    }
  }, [api, dirty, facetKey, mutationIdFactory, numericValidation, onClose, onRecoveryChange, onSaved, state, t]);

  const requestClose = useCallback(() => {
    if (dirty) {
      setCloseGuard(true);
      return;
    }
    onClose();
  }, [dirty, onClose]);

  const discardAndClose = useCallback(() => {
    dispatch(discarded());
    if (onRecoveryChange) onRecoveryChange(null);
    setCloseGuard(false);
    onClose();
  }, [onClose, onRecoveryChange]);

  useEffect(() => {
    if (!facetKey || typeof document === 'undefined') return undefined;
    document.body.classList.add('fp-editor-open');
    if (closeRef.current) closeRef.current.focus();
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (event.target.closest('.fp-dialog, .fp-group-rename-form')) return;
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('fp-editor-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [facetKey, requestClose]);

  useEffect(() => {
    if (closeGuard && keepEditingRef.current) keepEditingRef.current.focus();
  }, [closeGuard]);

  useEffect(() => {
    if (!state.save.error || state.save.status === 'recoverable') return undefined;
    const timer = window.setTimeout(() => dispatch(saveErrorDismissed()), 5000);
    return () => window.clearTimeout(timer);
  }, [state.save.error, state.save.status]);

  const { draft } = state.openPolicy;
  const applyBasicsDraft = useCallback((nextDraft) => {
    if (draft && nextDraft.type !== draft.type) {
      dispatch(facetTypeChanged({
        type: nextDraft.type,
        trueLabel: t('facets.policy.boolean.yes', 'Yes'),
        falseLabel: t('facets.policy.boolean.no', 'No'),
      }));
      setNumericValidation(null);
      setValidationIssues([]);
      return;
    }
    patchMetadata({
      title: nextDraft.title,
      visible: nextDraft.visible,
      searchable: nextDraft.searchable,
    });
  }, [draft, patchMetadata, t]);
  const editorProps = {
    baseline: state.openPolicy.baseline ? state.openPolicy.baseline.policy : null,
    draft,
    delta: state.delta,
    dispatch,
    api,
    facetKey,
    validationIssues,
    effect,
    onValidationChange: setNumericValidation,
    onRequestReindex,
    values: groupValues.items,
    nextCursor: groupValues.nextCursor,
    valuesStatus: groupValues.status,
    onSearch: query => loadGroupValues(query, null, false),
    onLoadPage: cursor => loadGroupValues(groupValues.query, cursor, true),
    onPolicyChange: patchValuePolicy,
  };

  const sourceValues = useMemo(() => groupValues.items.map((item) => ({
    id: String(item.source_value_id),
    label: item.display_label || item.source_label || item.label
      || t('facets.policy.editor.values.unavailable', 'Unavailable value'),
    meta: item.external_id == null ? null : `#${item.external_id}`,
    count: Number(item.product_count ?? item.count ?? 0),
    source: item,
  })), [groupValues.items, t]);

  const booleanValues = useMemo(() => sourceValues.map((value) => {
    const canonical = effectiveCanonicalValue(value.source, state.delta);
    return {
      ...value,
      lane: canonical === 'true' ? 'yes' : canonical === 'false' ? 'no' : 'unassigned',
    };
  }), [sourceValues, state.delta]);

  const groupModel = useMemo(() => {
    const policy = draft && draft.value_policy ? draft.value_policy : {};
    const groups = (policy.groups || []).map(group => ({
      id: String(group.value),
      label: group.label || String(group.value),
      color: group.visual && group.visual.kind === 'color' && group.visual.value
        ? group.visual.value : '#8798a5',
      values: [],
    }));
    const groupsById = groups.reduce((result, group) => ({ ...result, [group.id]: group }), {});
    const ungrouped = [];
    sourceValues.forEach((value) => {
      const canonical = effectiveCanonicalValue(value.source, state.delta);
      if (canonical != null && groupsById[String(canonical)]) {
        groupsById[String(canonical)].values.push(value);
      } else {
        ungrouped.push(value);
      }
    });
    return { groups, ungrouped };
  }, [draft, sourceValues, state.delta]);

  const displayOrder = useMemo(() => {
    const policy = draft && draft.value_policy ? draft.value_policy : {};
    const pinned = (policy.pinned || []).map(String);
    const hidden = (policy.hidden || []).map(String);
    const labels = {};
    const counts = {};
    (policy.groups || []).forEach((group) => {
      const value = String(group.value);
      labels[value] = group.label || value;
      counts[value] = 0;
    });
    sourceValues.forEach((sourceValue) => {
      const assignedCanonical = effectiveCanonicalValue(sourceValue.source, state.delta);
      const passThroughCanonical = sourceValue.source.pass_through_canonical_value == null
        ? null : String(sourceValue.source.pass_through_canonical_value);
      const canonical = assignedCanonical == null
        && policy.unmapped === 'pass_through' ? passThroughCanonical : assignedCanonical;
      if (canonical == null) return;
      const value = String(canonical);
      labels[value] = labels[value]
        || sourceValue.source.canonical_label
        || sourceValue.label
        || value;
      counts[value] = Number(counts[value] || 0) + sourceValue.count;
      if (passThroughCanonical === value) {
        labels[value] = sourceValue.label || labels[value];
      }
    });
    pinned.concat(hidden).forEach((value) => {
      if (!labels[value]) labels[value] = t('facets.policy.editor.values.unavailable', 'Unavailable value');
      if (!Number.isFinite(counts[value])) counts[value] = 0;
    });
    const ordered = [
      ...pinned,
      ...Object.keys(labels).filter(value => !pinned.includes(value) && !hidden.includes(value)),
      ...hidden.filter(value => !pinned.includes(value)),
    ];
    return {
      items: ordered.filter((value, index) => ordered.indexOf(value) === index).map(value => ({
        id: value,
        label: labels[value],
        count: Number(counts[value] || 0),
        pinned: pinned.includes(value),
        hidden: hidden.includes(value),
        sourceValueId: sourceValues.find(sourceValue => (
          String(sourceValue.source.pass_through_canonical_value || '') === value
        ))?.id || null,
      })),
      sort: policy.sort_remaining_by === 'alpha_asc' ? 'az' : 'count-desc',
    };
  }, [draft, sourceValues, state.delta, t]);

  const applyBooleanValues = useCallback((values) => {
    values.forEach((value) => {
      const current = effectiveCanonicalValue(value.source, state.delta);
      const canonical = value.lane === 'yes' ? 'true' : value.lane === 'no' ? 'false' : null;
      if (canonical === current) return;
      if (canonical == null) dispatch(assignmentCleared({ sourceValueId: value.id }));
      else dispatch(assignmentChanged({ sourceValueId: value.id, canonicalValue: canonical }));
    });
  }, [state.delta]);

  const applyGroups = useCallback((next) => {
    const currentGroups = (draft.value_policy && draft.value_policy.groups) || [];
    const nextIds = new Set(next.groups.map(group => String(group.id)));
    const deletedIds = new Set();
    currentGroups.forEach((group) => {
      const id = String(group.value);
      if (!nextIds.has(id)) {
        deletedIds.add(id);
        dispatch(groupDeleted({
          groupId: id,
          sourceValueIds: [],
        }));
      }
    });
    patchValuePolicy({
      groups: next.groups.map(group => ({
        value: String(group.id),
        label: group.label,
        visual: draft.type === 'color' ? { kind: 'color', value: String(group.color).toUpperCase() } : null,
      })),
      pinned: (draft.value_policy.pinned || []).filter(value => !deletedIds.has(String(value))),
      hidden: (draft.value_policy.hidden || []).filter(value => !deletedIds.has(String(value))),
    });
    const targetByValue = next.groups.reduce((result, group) => {
      group.values.forEach((value) => { result[value.id] = String(group.id); });
      return result;
    }, {});
    sourceValues.forEach((value) => {
      const current = effectiveCanonicalValue(value.source, state.delta);
      const target = targetByValue[value.id] || null;
      if (target === current) return;
      if (target == null && deletedIds.has(String(current))) return;
      if (target == null) {
        if (current != null && currentGroups.some(group => String(group.value) === current)) {
          dispatch(assignmentCleared({ sourceValueId: value.id }));
        }
      } else {
        dispatch(assignmentChanged({ sourceValueId: value.id, canonicalValue: target }));
      }
    });
  }, [draft, patchValuePolicy, sourceValues, state.delta]);

  const applyDisplayOrder = useCallback((value) => {
    value.items.forEach((item) => {
      if (!item.sourceValueId) return;
      const sourceValue = sourceValues.find(candidate => candidate.id === item.sourceValueId);
      if (!sourceValue) return;
      const assignedCanonical = effectiveCanonicalValue(sourceValue.source, state.delta);
      if (item.pinned || item.hidden) {
        if (assignedCanonical !== item.id) {
          dispatch(assignmentChanged({ sourceValueId: item.sourceValueId, canonicalValue: item.id }));
        }
      } else if (assignedCanonical === item.id) {
        dispatch(assignmentCleared({ sourceValueId: item.sourceValueId }));
      }
    });
    patchValuePolicy({
      pinned: value.items.filter(item => item.pinned && !item.hidden).map(item => String(item.id)),
      hidden: value.items.filter(item => item.hidden).map(item => String(item.id)),
      sort_remaining_by: value.sort === 'az' ? 'alpha_asc' : 'count_desc',
    });
  }, [patchValuePolicy, sourceValues, state.delta]);

  useEffect(() => {
    if (draft && draft.type !== 'string' && section === 'display') setSection('values');
  }, [draft, section]);

  const tabs = draft ? [
    ['basics', t('facets.policy.editor.section.metadata', 'Basics')],
    ['values', draft.type === 'number'
      ? t('facets.policy.editor.number.dataCheck', 'Data check')
      : t('facets.policy.editor.section.values', 'Values')],
    ...(draft.type === 'string' ? [['display', t('facets.policy.editor.displayOrder', 'Display order')]] : []),
  ] : [];

  if (!facetKey) return null;

  const productionEditor = (
    <div className="facets-prototype fp-editor-backdrop" role="presentation" onMouseDown={requestClose}>
      <aside
        ref={editorRef}
        className="fp-full-editor"
        role="dialog"
        aria-modal="true"
        aria-hidden={closeGuard || undefined}
        aria-label={t('facets.policy.editor.title', 'Facet policy editor')}
        data-testid="facet-policy-editor-shell"
        onMouseDown={event => event.stopPropagation()}
        onKeyDown={trapTabInside}
      >
        <header className="fp-editor-topbar">
          <div className="fp-editor-identity">
            <span className={`fp-type-icon fp-type-icon--${draft ? draft.type : 'string'}`}>
              <TypeIcon type={draft ? draft.type : 'string'} />
            </span>
            <div>
              <span className="fp-eyebrow">{t('facets.policy.editor.filter', 'Filter')}</span>
              <h1>{draft ? draft.title || facetKey : facetKey}</h1>
            </div>
          </div>
          <div className="fp-editor-actions">
            {draft && (
              <button
                type="button"
                className="fp-button fp-button--primary"
                aria-label={t('facets.policy.editor.save', 'Save policy')}
                disabled={!dirty || state.save.status === 'saving'
                  || (requiresNumericValidation && !numericValidation)}
                onClick={save}
              >
                {state.save.status === 'saving'
                  ? t('facets.policy.editor.saving', 'Saving…')
                  : t('facets.policy.editor.saveChanges', 'Save changes')}
              </button>
            )}
            <button
              ref={closeRef}
              type="button"
              className="fp-icon-button fp-editor-close"
              aria-label={t('facets.policy.editor.close', 'Close facet policy editor')}
              onClick={requestClose}
            ><Close /></button>
          </div>
        </header>

        {draft && (
          <nav className="fp-editor-tabs" aria-label={t('facets.policy.editor.sections', 'Filter editor sections')}>
            {tabs.map(([id, label]) => (
              <button
                type="button"
                key={id}
                className={section === id ? 'is-active' : ''}
                aria-current={section === id ? 'page' : undefined}
                onClick={() => setSection(id)}
              >{label}</button>
            ))}
          </nav>
        )}

        <div className="fp-editor-body">
          <main>
            {state.openPolicy.status === 'loading' && (
              <div className="fp-validation-empty is-loading" role="status">
                <span className="fp-spinner" />
                <h3>{t('facets.policy.editor.loading', 'Loading filter…')}</h3>
              </div>
            )}
            {state.openPolicy.status === 'failed' && <div className="fp-production-notice is-error" role="alert">{state.openPolicy.error}</div>}
            {state.openPolicy.status === 'succeeded' && !draft && <div className="fp-production-notice" role="status">{t('facets.policy.editor.empty', 'Facet policy is empty.')}</div>}

            {draft && section === 'basics' && (
              <div data-testid="facet-policy-metadata">
                <BasicsEditor
                  draft={draft}
                  setDraft={applyBasicsDraft}
                  onContinue={() => setSection('values')}
                  translate={t}
                >
                  {draft.type === 'string' && (
                    <StringPresentationPicker
                      value={draft.value_policy.presentation || 'list'}
                      onChange={presentation => patchValuePolicy({ presentation })}
                      enabled={stringPresentationEnabled}
                    />
                  )}
                </BasicsEditor>
              </div>
            )}

            {draft && section === 'values' && (
              <div className="fp-editor-section fp-editor-section--wide">
                <header className="fp-section-heading">
                  <span className="fp-eyebrow">{t('facets.policy.editor.section.values', 'Values')}</span>
                  <h2>{draft.type === 'number'
                    ? t('facets.policy.editor.number.validateRange', 'Validate the range')
                    : t('facets.policy.editor.values.clearChoices', 'Turn catalog data into clear choices')}</h2>
                </header>
                {draft.type === 'number' && !numericEnabled && (
                  <div className="fp-production-notice" role="status">{t('facets.policy.editor.numericUnavailable', 'Numeric filters are unavailable for this installation.')}</div>
                )}
                {draft.type !== 'number' && groupValues.status === 'loading' && sourceValues.length === 0 && (
                  <div className="fp-validation-empty is-loading" role="status"><span className="fp-spinner" /><h3>{t('facets.policy.editor.values.loading', 'Loading values…')}</h3></div>
                )}
                {draft.type === 'boolean' && sourceValues.length > 0 && <BooleanMapper values={booleanValues} onChange={applyBooleanValues} translate={t} />}
                {(draft.type === 'string' || draft.type === 'color') && sourceValues.length > 0 && (
                  <GroupEditor type={draft.type} {...groupModel} onChange={applyGroups} translate={t} />
                )}
                {draft.type === 'number' && numericEnabled && (
                  <NumberFacetPolicyEditor key={numericValidationEpoch} {...editorProps} />
                )}
              </div>
            )}

            {draft && section === 'display' && (
              groupValues.status === 'loading' && sourceValues.length === 0
                ? <div className="fp-validation-empty is-loading" role="status"><span className="fp-spinner" /><h3>{t('facets.policy.editor.values.loading', 'Loading values…')}</h3></div>
                : <DisplayOrderEditor value={displayOrder} onChange={applyDisplayOrder} translate={t} />
            )}

            {state.save.error && state.save.status !== 'recoverable' && (
              <div className="fp-error-toast" role="alert">
                <div>
                  <strong>{t('facets.policy.editor.saveFailed', 'Changes were not saved')}</strong>
                  <span>{validationIssues[0] && (validationIssues[0].reason || validationIssues[0].message)
                    ? validationIssues[0].reason || validationIssues[0].message
                    : state.save.error.message}</span>
                </div>
                <button type="button" className="fp-icon-button" aria-label={t('common.close', 'Close')} onClick={() => dispatch(saveErrorDismissed())}><Close /></button>
              </div>
            )}
            {state.save.status === 'recoverable' && (
              <div className="fp-production-notice" role="status">{t('facets.policy.editor.conflict', 'This filter changed on the server. Your draft is preserved; review it and save again.')}</div>
            )}
            {state.save.status === 'succeeded' && <div className="fp-save-confirmation" role="status"><CheckIcon /> {t('facets.policy.editor.saved', 'Changes saved')}</div>}
          </main>
        </div>
      </aside>

      {closeGuard && (
        <div
          className="fp-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => { event.stopPropagation(); setCloseGuard(false); }}
        >
          <section
            className="fp-dialog fp-dialog--discard"
            role="alertdialog"
            aria-modal="true"
            aria-label={t('facets.policy.editor.unsaved', 'Unsaved facet policy')}
            onMouseDown={event => event.stopPropagation()}
            onKeyDown={trapTabInside}
          >
            <header><h2>{t('facets.policy.editor.discardTitle', 'Discard changes?')}</h2></header>
            <footer>
              <button ref={keepEditingRef} type="button" className="fp-button fp-button--quiet" onClick={() => setCloseGuard(false)}>{t('facets.policy.editor.keepEditing', 'Keep editing')}</button>
              <button type="button" className="fp-button fp-button--danger" onClick={discardAndClose}>{t('facets.policy.editor.discardClose', 'Discard and close')}</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );

  const capabilityUnavailable = (
    <aside role="dialog" aria-modal="true" aria-label={t('facets.policy.editor.title', 'Facet policy editor')}>
      <div role="status">{t('facets.policy.editor.capabilityUnavailable', 'Facet policy editor capability unavailable.')}</div>
      <button type="button" onClick={onClose}>{t('common.close', 'Close')}</button>
    </aside>
  );
  const renderedEditor = policyEnabled ? productionEditor : capabilityUnavailable;
  return typeof document === 'undefined' ? renderedEditor : createPortal(renderedEditor, document.body);
}

FacetPolicyEditorShell.propTypes = {
  api: PropTypes.shape({
    getPolicy: PropTypes.func.isRequired,
    updatePolicy: PropTypes.func.isRequired,
  }),
  facetKey: PropTypes.string,
  shopId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  languageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  capabilities: PropTypes.shape({
    facet_policy_v1: PropTypes.bool,
    facet_numeric_v1: PropTypes.bool,
    facet_string_presentation_v1: PropTypes.bool,
  }),
  recovery: PropTypes.string,
  onRecoveryChange: PropTypes.func,
  onDirtyChange: PropTypes.func,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
  onRequestReindex: PropTypes.func,
  mutationIdFactory: PropTypes.func,
};

FacetPolicyEditorShell.defaultProps = {
  api: null,
  facetKey: null,
  capabilities: DEFAULT_CAPABILITIES,
  recovery: null,
  onRecoveryChange: null,
  onDirtyChange: null,
  onClose: () => {},
  onSaved: () => {},
  onRequestReindex: () => {},
  mutationIdFactory: generatedMutationId,
};

BooleanBulkAssignmentEditor.propTypes = {
  api: PropTypes.shape({ searchValues: PropTypes.func.isRequired }),
  facetKey: PropTypes.string,
  delta: PropTypes.shape({
    assignmentChanges: PropTypes.objectOf(PropTypes.string),
    clearAssignmentIds: PropTypes.objectOf(PropTypes.bool),
    clearAllAssignments: PropTypes.bool,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  validationIssues: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.string,
    source_value_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    message: PropTypes.string,
    reason: PropTypes.string,
  })),
};

BooleanBulkAssignmentEditor.defaultProps = {
  api: null,
  facetKey: null,
  validationIssues: [],
};
